import { useEffect, useState } from "react";
import { Spin } from "antd";
import { useAuth } from "@adapters/hooks";
import Login from "@pages/Login";
import styles from "./AuthGuard.module.less";

interface AuthGuardProps {
  children: React.ReactNode;
}

export const AuthGuard = ({ children }: AuthGuardProps) => {
  const auth = useAuth();
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check auth immediately
    const checkAuth = () => {
      const token = auth.getToken();
      const currentUser = auth.getCurrentUser();
      const authenticated = !!token && !!currentUser;
      
      setIsAuthenticated(authenticated);
      setIsChecking(false);
    };

    // Initial check - synchronous to avoid flash
    checkAuth();

    // Listen for auth changes (will be triggered immediately when login/logout happens)
    // This ensures smooth transition without page reload
    const unsubscribe = auth.onAuthChange((user) => {
      const token = auth.getToken();
      const authenticated = !!token && !!user;
      setIsAuthenticated(authenticated);
      setIsChecking(false);
    });

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'chat_auth_token' || e.key === 'auth_token' || e.key === 'chat_auth_user') {
        checkAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [auth]);

  // Show loading spinner instead of null to avoid flash
  if (isChecking) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className={styles.authGuardContainer}>
        <Login />
      </div>
    );
  }

  return (
    <div className={styles.authGuardContainer}>
      {children}
    </div>
  );
};

